/**
 * SAMPLE DATA ONLY. Nothing here is real: names, counts and rupees are made up so the
 * layout can be judged before any backend exists. Generated from a fixed seed, so the
 * numbers are the same on every reload and always add up.
 */
import type {
  AutomationCounts, BucketKey, CatalogAgent, CatalogCategory, CatalogMetrics, CatalogProduct,
  CallRow, DrillContext, StageKey, TrendPoint, WorkRow,
} from './types';
import { addStages, emptyStages } from './adapt';
import { WORK_BUCKETS } from './theme';

export function rng(seed: number) {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export const hash = (s: string) => { let h = 7; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return h; };

export const AGENTS = ['Agent A', 'Agent B', 'Agent C', 'Agent D', 'Agent E', 'Agent F'];

/** Which buckets each sample agent works, as a volume weight (1 = a full load). */
const LOAD: Record<string, Partial<Record<BucketKey, number>>> = {
  'Agent A': { abc: 1.0, reorder: 0.9, rto: 0.8, agent_lead: 0.4 },
  'Agent B': { abc: 0.9, reorder: 1.1, rto: 0.7, phone: 0.5 },
  'Agent C': { abc: 1.1, reorder: 0.8, agent_lead: 0.8, phone: 0.6 },
  'Agent D': { abc: 0.8, reorder: 1.0, edd: 1.0, ndr: 1.0 },
  'Agent E': { abc: 1.0, edd: 0.9, ndr: 0.9, rto: 0.5 },
  'Agent F': { agent_lead: 1.0, phone: 1.1, reorder: 0.6, abc: 0.6 },
};

interface BucketModel { base: number; workRate: number; orderRate: number; aov: number }
const MODEL: Record<BucketKey, BucketModel> = {
  abc:        { base: 140, workRate: 0.72, orderRate: 0.15, aov: 1450 },
  reorder:    { base: 60,  workRate: 0.85, orderRate: 0.38, aov: 1100 },
  rto:        { base: 22,  workRate: 0.70, orderRate: 0.30, aov: 1650 },
  edd:        { base: 70,  workRate: 0.66, orderRate: 0.02, aov: 1300 },
  ndr:        { base: 34,  workRate: 0.62, orderRate: 0.03, aov: 1300 },
  agent_lead: { base: 40,  workRate: 0.90, orderRate: 0.45, aov: 1900 },
  phone:      { base: 28,  workRate: 0.80, orderRate: 0.40, aov: 1500 },
};

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Sample only: split valid orders into delivery stages with fixed shares, so the stages add up to booked. */
export function sampleCommercial(valid: number, cancelled: number, revenue: number, cancelledValue: number) {
  const shares: [Exclude<StageKey, 'cancelled'>, number][] = [['delivered', 0.55], ['inTransit', 0.25], ['awaitingPickup', 0.1], ['returned', 0.1]];
  const stages = emptyStages();
  let usedO = 0, usedV = 0;
  shares.forEach(([k, f], i) => {
    const last = i === shares.length - 1;
    const o = last ? valid - usedO : Math.round(valid * f);
    const v = last ? revenue - usedV : Math.round(revenue * f);
    stages[k] = { orders: o, value: v }; usedO += o; usedV += v;
  });
  stages.cancelled = { orders: cancelled, value: cancelledValue };
  const share = 0.85;
  const tag = emptyStages();
  (Object.keys(stages) as StageKey[]).forEach(k => { tag[k] = { orders: Math.round(stages[k].orders * share), value: Math.round(stages[k].value * share) }; });
  return { booked: revenue + cancelledValue, stages, tag };
}

function buildRows(): WorkRow[] {
  const r = rng(20261012);
  const rows: WorkRow[] = [];
  for (const agent of AGENTS) {
    for (const b of WORK_BUCKETS) {
      const w = LOAD[agent][b];
      if (!w) continue;
      const m = MODEL[b];
      const received = Math.round(m.base * w * (0.9 + r() * 0.2));
      const worked = clamp(Math.round(received * m.workRate * (0.92 + r() * 0.14)), 0, received);
      // Calls placed through the dialer are credited to the agent who made them.
      const tataShare = b === 'abc' || b === 'phone' || b === 'reorder' ? 0.18 + r() * 0.2 : 0.05 + r() * 0.05;
      const workedTata = Math.round(worked * tataShare);
      const unworked = received - worked;
      const closedBySystem = Math.round(unworked * (0.2 + r() * 0.1));
      const closedByAgent = clamp(Math.round(worked * (0.55 + r() * 0.2)), 0, received - closedBySystem);

      // Counting units. ABC: one work item per cart event. Reorder: one item per
      // product due date, so a customer can hold several. EDD/NDR/RTO: items can outnumber orders
      // (an order can re-open the same issue). Agent Lead / Phone: one item per lead.
      const workItems = received;
      let uniqueOrders: number | null = null, uniqueCustomers = received;
      if (b === 'abc') { uniqueCustomers = Math.round(received / (1.05 + r() * 0.1)); }
      else if (b === 'reorder') { uniqueCustomers = Math.round(received * (0.74 + r() * 0.12)); }
      else if (b === 'edd' || b === 'ndr' || b === 'rto') {
        uniqueOrders = Math.round(received * (0.82 + r() * 0.1));
        uniqueCustomers = Math.round(uniqueOrders * (0.86 + r() * 0.08));
      } else { uniqueCustomers = Math.round(received * (0.95 + r() * 0.05)); }

      const orders = Math.round(received * m.orderRate * (0.85 + r() * 0.3));
      const cancelledOrders = Math.round(orders * (0.07 + r() * 0.06));
      const validOrders = orders - cancelledOrders;
      const tagOrders = Math.round(orders * (0.84 + r() * 0.08));
      const validRevenue = Math.round((validOrders * m.aov * (0.9 + r() * 0.2)) / 10) * 10;
      const cancelledValue = Math.round((cancelledOrders * m.aov) / 10) * 10;
      rows.push({
        agent, bucket: b, received, workItems, uniqueCustomers, uniqueOrders,
        worked, workedTata, workedUnsplit: 0, workedManual: worked - workedTata, closedByAgent, closedBySystem,
        orders, validOrders, cancelledOrders,
        units: Math.round(validOrders * (1.25 + r() * 0.3)),
        validRevenue, cancelledValue,
        tagOrders, couponOrders: orders - tagOrders,
        ...sampleCommercial(validOrders, cancelledOrders, validRevenue, cancelledValue),
      });
    }
  }
  return rows;
}

export const ROWS: WorkRow[] = buildRows();

/**
 * Orders with no valid agent tag are not agent work, so they are not in this report.
 * They are named in one footnote so the company figure can still be reconciled.
 */
export const NO_AGENT_ORDERS = { orders: 340, value: 469200 };

/** Automation is counted and shown, but is never "worked". */
export const AUTOMATION: AutomationCounts = { engatiAi: 214, abcAutoUpdated: 96, otherSystem: 31 };

export function buildTrend(valid: number, cancelled: number): TrendPoint[] {
  const r = rng(77);
  const days = 14;
  const out: TrendPoint[] = [];
  const d = new Date();
  d.setDate(d.getDate() - (days - 1));
  for (let i = 0; i < days; i++) {
    const weekend = d.getDay() === 0 || d.getDay() === 6;
    const f = (weekend ? 0.7 : 1.05) * (0.9 + r() * 0.2);
    out.push({
      day: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      valid: Math.round((valid / days) * f),
      cancelled: Math.round((cancelled / days) * f * (0.8 + r() * 0.4)),
    });
    d.setDate(d.getDate() + 1);
  }
  return out;
}

/** Bucket-specific outcomes: ABC and Reorder do not share one flattened tree. */
export const DISPOSITIONS: Record<BucketKey, string[]> = {
  abc: ['Interested – Call Back', 'Order Placed', 'Not Connected – Ringing', 'Not Interested', 'Already Purchased'],
  reorder: ['Will Reorder', 'Reorder Placed', 'Not Reachable', 'Price Concern'],
  rto: ['Agreed – Re-ship', 'Refused Delivery', 'Not Reachable', 'Order Cancelled'],
  edd: ['Informed – Delivery Soon', 'Not Reachable', 'Address Issue'],
  ndr: ['Reattempt Requested', 'Customer Unavailable', 'Address Corrected', 'RTO Confirmed'],
  agent_lead: ['Interested', 'Follow-up Scheduled', 'Not Interested'],
  phone: ['Enquiry Resolved', 'Order Placed', 'Callback Requested'],
};

/** Three-level outcomes (L1 > L2 > L3) per bucket, used for the timeline columns in downloads. */
export const DISPOSITION_TREE: Record<BucketKey, [string, string, string][]> = {
  abc: [['Connected', 'Interested', 'Call Back'], ['Connected', 'Order Placed', 'Prepaid'], ['Connected', 'Not Interested', 'Price'],
        ['Not Connected', 'Ringing', 'Attempt 1'], ['Not Connected', 'Ringing', 'Attempt 2'], ['Not Connected', 'Switched Off', '']],
  reorder: [['Connected', 'Will Reorder', 'This Week'], ['Connected', 'Reorder Placed', ''], ['Not Connected', 'Not Reachable', 'Attempt 1'], ['Connected', 'Price Concern', '']],
  rto: [['Connected', 'Agreed', 'Re-ship'], ['Connected', 'Refused Delivery', ''], ['Not Connected', 'Not Reachable', 'Attempt 1'], ['Connected', 'Order Cancelled', '']],
  edd: [['Connected', 'Informed', 'Delivery Soon'], ['Not Connected', 'Not Reachable', 'Attempt 1'], ['Connected', 'Address Issue', '']],
  ndr: [['Connected', 'Reattempt Requested', ''], ['Connected', 'Customer Unavailable', ''], ['Connected', 'Address Corrected', ''], ['Connected', 'RTO Confirmed', '']],
  agent_lead: [['Connected', 'Interested', ''], ['Connected', 'Follow-up Scheduled', ''], ['Connected', 'Not Interested', '']],
  phone: [['Connected', 'Enquiry Resolved', ''], ['Connected', 'Order Placed', ''], ['Connected', 'Callback Requested', '']],
};

export const ALL_DISPOSITIONS = Array.from(new Set(WORK_BUCKETS.flatMap(b => DISPOSITIONS[b])));

// ---- Category > Product > Agent --------------------------------------------------------

const PRODUCT_LIST: { category: string; product: string; w: number }[] = [
  { category: 'Metabolism & Diabetes', product: 'Sugar Balance Capsules', w: 3 },
  { category: 'Metabolism & Diabetes', product: 'Gluco Care Tea', w: 1.5 },
  { category: 'Detox', product: 'Liver Detox Drops', w: 2 },
  { category: 'Detox', product: 'Full Body Cleanse Kit', w: 1 },
  { category: 'Women Wellness', product: 'Cycle Care Capsules', w: 2.2 },
  { category: 'Women Wellness', product: 'PCOS Balance Powder', w: 1.8 },
  { category: 'Fertility', product: 'Conceive Plus', w: 1.2 },
  { category: 'Fertility', product: 'Prenatal Blend', w: 0.8 },
  { category: 'Sexual Wellness', product: 'Vigor Gold Capsules', w: 2.6 },
  { category: 'Sexual Wellness', product: 'Stamina Oil', w: 1 },
  { category: 'Beauty', product: 'Hair Growth Serum', w: 1.6 },
  { category: 'Beauty', product: 'Glow Collagen', w: 1.4 },
  { category: 'Gut Health', product: 'Digest Ease Powder', w: 1.8 },
  { category: 'Gut Health', product: 'Probiotic Plus', w: 1.1 },
  { category: 'Longevity', product: 'Shilajit Resin', w: 2.4 },
  { category: 'Longevity', product: 'NAD Boost', w: 0.9 },
  { category: 'Vitamins & Minerals', product: 'Daily Multi', w: 1.5 },
  { category: 'Vitamins & Minerals', product: 'D3 + K2', w: 1 },
  { category: 'Others', product: 'Combo Pack', w: 1.3 },
  { category: 'Others', product: 'Trial Sachets', w: 0.5 },
];
export const CATEGORIES = Array.from(new Set(PRODUCT_LIST.map(p => p.category)));
export const productsOf = (category: string) =>
  PRODUCT_LIST.filter(p => category === 'all' || p.category === category).map(p => p.product);

/** Split an integer across weights so the parts always add back to the whole. */
export function alloc(total: number, weights: number[]): number[] {
  const sum = weights.reduce((a, b) => a + b, 0) || 1;
  const parts = weights.map(w => Math.floor((total * w) / sum));
  let rest = total - parts.reduce((a, b) => a + b, 0);
  const order = weights.map((w, i) => [w, i] as const).sort((a, b) => b[0] - a[0]);
  for (let k = 0; rest > 0; k = (k + 1) % order.length, rest--) parts[order[k][1]] += 1;
  return parts;
}

const add = (a: CatalogMetrics, b: CatalogMetrics): CatalogMetrics => ({
  orders: a.orders + b.orders, valid: a.valid + b.valid, cancelled: a.cancelled + b.cancelled, units: a.units + b.units,
  revenue: a.revenue + b.revenue, cancelledValue: a.cancelledValue + b.cancelledValue, workItems: a.workItems + b.workItems,
  booked: a.booked + b.booked, stages: addStages(a.stages, b.stages),
});
const ZERO: CatalogMetrics = { orders: 0, valid: 0, cancelled: 0, units: 0, revenue: 0, cancelledValue: 0, workItems: 0, booked: 0, stages: emptyStages() };

const sampleCommercialOf = (valid: number, cancelled: number, revenue: number, cancelledValue: number) => {
  const { booked, stages } = sampleCommercial(valid, cancelled, revenue, cancelledValue);
  return { booked, stages };
};

/** Each agent's orders are spread over products, so category and product totals tie back to the agent rows. */
export function buildCatalog(rows: WorkRow[], category: string, product: string): CatalogCategory[] {
  const perProduct = new Map<string, CatalogAgent[]>();
  for (const agent of AGENTS) {
    const mine = rows.filter(r => r.agent === agent);
    if (mine.length === 0) continue;
    const sum = (f: (r: WorkRow) => number) => mine.reduce((a, r) => a + f(r), 0);
    const r = rng(hash(agent + 'catalog'));
    const weights = PRODUCT_LIST.map(p => p.w * (0.6 + r() * 0.8));
    const valid = alloc(sum(x => x.validOrders), weights);
    const canc = alloc(sum(x => x.cancelledOrders), weights);
    // Money and units follow the orders they belong to, so no product shows revenue with no orders.
    const units = alloc(sum(x => x.units), valid);
    const rev = alloc(Math.round(sum(x => x.validRevenue) / 10), valid);
    const cval = alloc(Math.round(sum(x => x.cancelledValue) / 10), canc);
    const work = alloc(sum(x => (x.bucket === 'abc' || x.bucket === 'reorder' ? x.workItems : 0)), weights);
    PRODUCT_LIST.forEach((p, i) => {
      const a: CatalogAgent = {
        agent, orders: valid[i] + canc[i], valid: valid[i], cancelled: canc[i], units: units[i],
        revenue: rev[i] * 10, cancelledValue: cval[i] * 10, workItems: work[i],
        ...sampleCommercialOf(valid[i], canc[i], rev[i] * 10, cval[i] * 10),
      };
      perProduct.set(p.product, [...(perProduct.get(p.product) ?? []), a]);
    });
  }
  const out: CatalogCategory[] = [];
  for (const cat of CATEGORIES) {
    if (category !== 'all' && cat !== category) continue;
    const products: CatalogProduct[] = PRODUCT_LIST.filter(p => p.category === cat && (product === 'all' || p.product === product))
      .map(p => {
        const agents = (perProduct.get(p.product) ?? []).filter(a => a.orders > 0 || a.workItems > 0);
        const m = agents.reduce<CatalogMetrics>((acc, a) => add(acc, a), ZERO);
        return { product: p.product, ...m, agents: agents.sort((a, b) => b.revenue - a.revenue) };
      }).filter(p => p.orders > 0 || p.workItems > 0).sort((a, b) => b.revenue - a.revenue);
    if (products.length === 0) continue;
    out.push({ category: cat, ...products.reduce<CatalogMetrics>((acc, p) => add(acc, p), ZERO), products });
  }
  return out.sort((a, b) => b.revenue - a.revenue);
}

// ---- Drilldown samples -----------------------------------------------------------------

export interface SampleWorkRow {
  id: string; phone: string; agent: string; bucket: BucketKey; receivedAt: string;
  state: 'Worked' | 'Unworked'; source: string; disposition: string;
}
export interface SampleOrderRow {
  order: string; agent: string; bucket: BucketKey; product: string;
  attribution: 'Agent Tag' | 'Coupon'; status: 'Valid' | 'Cancelled'; value: number;
}

export function sampleWork(ctx: DrillContext, limit = 12): SampleWorkRow[] {
  const r = rng(hash(ctx.title + ctx.agent + ctx.bucket));
  const n = Math.min(limit, ctx.total);
  const out: SampleWorkRow[] = [];
  for (let i = 0; i < n; i++) {
    const bucket = (ctx.bucket === 'all' ? WORK_BUCKETS[Math.floor(r() * WORK_BUCKETS.length)] : ctx.bucket) as BucketKey;
    const worked = ctx.worked === 'worked' ? true : ctx.worked === 'unworked' ? false : r() < 0.7;
    const agent = ctx.agent === 'all' ? AGENTS[Math.floor(r() * AGENTS.length)] : ctx.agent;
    const d = new Date(); d.setHours(8 + Math.floor(r() * 11), Math.floor(r() * 60), 0, 0);
    d.setDate(d.getDate() - Math.floor(r() * 7));
    const opts = DISPOSITIONS[bucket];
    out.push({
      id: `WK-${(48000 + Math.floor(r() * 9000)).toString()}`,
      phone: `9${Math.floor(r() * 9) + 1}•••••${Math.floor(1000 + r() * 8999)}`.slice(0, 12),
      agent, bucket,
      receivedAt: d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
      state: worked ? 'Worked' : 'Unworked',
      source: worked ? (r() < 0.75 ? 'MANUAL' : 'TATA_TELE') : '—',
      disposition: worked ? opts[Math.floor(r() * opts.length)] : '—',
    });
  }
  return out;
}

export function sampleOrders(ctx: DrillContext, limit = 12): SampleOrderRow[] {
  const r = rng(hash(ctx.title + ctx.agent + ctx.bucket + 'o'));
  const n = Math.min(limit, ctx.total);
  const out: SampleOrderRow[] = [];
  for (let i = 0; i < n; i++) {
    const bucket = (ctx.bucket === 'all' ? WORK_BUCKETS[Math.floor(r() * WORK_BUCKETS.length)] : ctx.bucket) as BucketKey;
    const agent = ctx.agent === 'all' ? AGENTS[Math.floor(r() * AGENTS.length)] : ctx.agent;
    const cancelled = ctx.orderStatus === 'cancelled' ? true : ctx.orderStatus === 'valid' ? false : r() < 0.1;
    out.push({
      order: `#ZH${633000 + Math.floor(r() * 9000)}`, agent, bucket,
      product: PRODUCT_LIST[Math.floor(r() * PRODUCT_LIST.length)].product,
      attribution: r() < 0.86 ? 'Agent Tag' : 'Coupon',
      status: cancelled ? 'Cancelled' : 'Valid',
      value: Math.round((900 + r() * 1800) / 10) * 10,
    });
  }
  return out;
}


// ---- Calls ------------------------------------------------------------------------------

interface CallModel { attempts: number; connect: number; avgTalk: number }
const CALL_MODEL: Record<BucketKey, CallModel> = {
  abc:        { attempts: 2.6, connect: 0.34, avgTalk: 95 },
  reorder:    { attempts: 2.0, connect: 0.52, avgTalk: 180 },
  rto:        { attempts: 2.4, connect: 0.38, avgTalk: 150 },
  edd:        { attempts: 1.8, connect: 0.45, avgTalk: 70 },
  ndr:        { attempts: 2.2, connect: 0.41, avgTalk: 110 },
  agent_lead: { attempts: 1.5, connect: 0.62, avgTalk: 220 },
  phone:      { attempts: 1.3, connect: 0.66, avgTalk: 260 },
};

/** Per-agent skill, so the agent view shows believable spread (some strong, some weak). */
const AGENT_SKILL: Record<string, { connect: number; talk: number; short: number }> = {
  'Agent A': { connect: 1.08, talk: 1.1, short: 0.8 },
  'Agent B': { connect: 1.0, talk: 1.0, short: 1.0 },
  'Agent C': { connect: 0.92, talk: 0.9, short: 1.2 },
  'Agent D': { connect: 1.04, talk: 1.05, short: 0.9 },
  'Agent E': { connect: 0.78, talk: 0.7, short: 2.1 },
  'Agent F': { connect: 1.12, talk: 1.25, short: 0.7 },
};

function buildCalls(): CallRow[] {
  const r = rng(555);
  return ROWS.map(w => {
    const m = CALL_MODEL[w.bucket as BucketKey];
    const sk = AGENT_SKILL[w.agent];
    const customersCalled = Math.max(1, Math.round(w.uniqueCustomers * (0.92 + r() * 0.08)));
    const dials = Math.round(customersCalled * m.attempts * (0.9 + r() * 0.2));
    const connected = clamp(Math.round(dials * m.connect * sk.connect * (0.93 + r() * 0.14)), 0, dials);
    const [noAnswer, cancelledRinging, droppedOther, hungUpEarly] = alloc(dials - connected, [14, 56, 28, 2]);
    return {
      agent: w.agent, bucket: w.bucket, dials, connected, noAnswer, cancelledRinging, droppedOther, hungUpEarly,
      talkSeconds: Math.round(connected * m.avgTalk * sk.talk * (0.9 + r() * 0.2)),
      shortCalls: Math.round(connected * 0.17 * sk.short * (0.9 + r() * 0.2)),
      ringSeconds: Math.round(dials * (11 + r() * 5)),
      customersCalled,
      customersConnected: clamp(Math.round(connected * (0.7 + r() * 0.1)), 0, customersCalled),
      activeDays: 18 + Math.floor(r() * 4),
    };
  });
}
export const CALLS: CallRow[] = buildCalls();

/** Connect rate and share of calls by hour of day (9 am to 8 pm). Sample shape: mid-morning is best. */
export const HOURLY: { h: number; hour: string; connect: number; share: number }[] = [
  [9, 0.27, 0.05], [10, 0.33, 0.10], [11, 0.41, 0.13], [12, 0.38, 0.11], [13, 0.30, 0.07], [14, 0.31, 0.09],
  [15, 0.35, 0.11], [16, 0.39, 0.12], [17, 0.43, 0.10], [18, 0.37, 0.07], [19, 0.29, 0.04], [20, 0.22, 0.01],
].map(([h, c, s]) => ({ h, hour: `${h > 12 ? h - 12 : h} ${h >= 12 ? 'pm' : 'am'}`, connect: c, share: s }));

/** Share of called customers reached on the 1st, 2nd, 3rd and 4th+ attempt; the rest were never reached. */
export const ATTEMPT_MIX = [0.34, 0.17, 0.10, 0.07];

export interface SampleCallRow {
  id: string; calledAt: string; agent: string; bucket: BucketKey; customer: string;
  outcome: string; talk: number; attempt: number;
}
export const NOT_CONNECTED_REASONS = ['No answer', 'Cancelled while ringing', 'Dropped / network', 'Hung up early'];

export function sampleCalls(ctx: DrillContext, limit = 12): SampleCallRow[] {
  const r = rng(hash(ctx.title + ctx.agent + ctx.bucket + 'c'));
  const n = Math.min(limit, ctx.total);
  const out: SampleCallRow[] = [];
  for (let i = 0; i < n; i++) {
    const bucket = (ctx.bucket === 'all' ? WORK_BUCKETS[Math.floor(r() * WORK_BUCKETS.length)] : ctx.bucket) as BucketKey;
    const agent = ctx.agent === 'all' ? AGENTS[Math.floor(r() * AGENTS.length)] : ctx.agent;
    const connected = ctx.callOutcome === 'connected' ? true : ctx.callOutcome === 'not_connected' ? false : r() < 0.42;
    const d = new Date(); d.setDate(d.getDate() - Math.floor(r() * 7)); d.setHours(9 + Math.floor(r() * 11), Math.floor(r() * 60), 0, 0);
    out.push({
      id: `CL-${70000 + Math.floor(r() * 9000)}`,
      calledAt: d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
      agent, bucket, customer: `9${Math.floor(r() * 9) + 1}•••••${Math.floor(1000 + r() * 8999)}`.slice(0, 12),
      outcome: connected ? 'Connected' : NOT_CONNECTED_REASONS[Math.floor(r() * NOT_CONNECTED_REASONS.length)],
      talk: connected ? Math.round(15 + r() * 320) : 0, attempt: 1 + Math.floor(r() * 4),
    });
  }
  return out;
}
